import { AuthPageBody } from '../../components/AuthPageBody/AuthPageBody';
import { Flex, Input, InputGroup, InputRightElement, FormLabel, Button, useToast, Link as ChakraLink } from '@chakra-ui/react';
import { WithFormControl } from 'src/components/WithFormControl';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { twoFactorFormSchema } from './schemas/twoFactorFormSchema';
import { TwoFactorFormSchemaData } from './schemas/types';
import { useAuthContext } from '@/contexts/AuthContext';
import { AuthFormBaseContainer } from '@/components/AuthFormBaseContainer/AuthFormBaseContainer';
import { useNavigate } from 'react-router';
import { use2FaMutation } from '@/services/queries/auth';
import { AuthenticationInfo } from '@/model/auth';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const TwoFactorAuth = (): JSX.Element => {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<TwoFactorFormSchemaData>({
    resolver: zodResolver(twoFactorFormSchema),
  });
  const toast = useToast();
  const { t } = useTranslation();

  const { login } = useAuthContext();
  const navigate = useNavigate();
  const twoFactorAuthMutation = use2FaMutation();

  const onSubmit: SubmitHandler<TwoFactorFormSchemaData> = async data => {
    twoFactorAuthMutation.mutate(data.code, {
      onSuccess: (authenticationInfo: AuthenticationInfo) => {
        login(authenticationInfo);
        toast({ status: 'success', title: t('pages.login.two_factor_auth.two_factor_success') });
        navigate('/');
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: resp?.message ?? t('pages.login.two_factor_auth.two_factor_error_title'),

          description: t('pages.login.two_factor_auth.two_factor_error_description'),
        });
      },
    });
  };

  return (
    <AuthPageBody>
      <AuthFormBaseContainer
        title={t('pages.login.two_factor_auth.two_factor_title')}
        description={t('pages.login.two_factor_auth.two_factor_description')}
      >
        <Flex
          onSubmit={handleSubmit(onSubmit)}
          as='form'
          direction='column'
          align='stretch'
          gap='small'
          px={5}
          w='full'
          maxW='400px'
          alignSelf='center'
        >
          <WithFormControl variant='floating' errorMessage={errors.code?.message} isInvalid={!!errors.code}>
            <InputGroup>
              <Input variant='flushed' {...register('code')} placeholder=' ' />
              <FormLabel>{t('pages.login.two_factor_auth.two_factor_code_label')}</FormLabel>

              <InputRightElement>
                <i className='uil-shield-check' />
              </InputRightElement>
            </InputGroup>
          </WithFormControl>

          <Flex alignItems='center' justifyContent='space-between' gap='8px'>
            <ChakraLink as={Link} to='/login' color='brand.500' fontSize='16px' textDecoration='underline'>
              {t('pages.login.two_factor_auth.two_factor_back_link')}
            </ChakraLink>

            <Button type='submit' w='208px' alignSelf='end' variant='gradient' size='md'>
              {t('pages.login.two_factor_auth.two_factor_submit_button')}
            </Button>
          </Flex>
        </Flex>
        <CenteredSpinner isLoading={twoFactorAuthMutation.isLoading} />
      </AuthFormBaseContainer>
    </AuthPageBody>
  );
};
